<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'

const {
    Button: PocketButton,
    Icon,
} = UIKit

const props = withDefaults(defineProps<{
    hasTakenExam?: boolean | null
    isMarketingCampaign?: boolean
    showingTryAQuestionNext?: boolean
}>(), {
    hasTakenExam: null,
    isMarketingCampaign: false,
    showingTryAQuestionNext: false,
})

const emit = defineEmits<{
    'updateHasTakenExam': [hasTakenExam: boolean | null]
    'submit': []
    'goBack': []
}>()

const updateHasTakenExam = (choice: boolean) => {
    const newHasTakenExam = props.hasTakenExam === choice ? null : choice
    emit('updateHasTakenExam', newHasTakenExam)
}

const submit = () => {
    emit('submit')
}

const goBack = () => {
    emit('goBack')
}
</script>

<template>
    <div class="first-time-exam-ask">
        <div class="first-time-exam-ask__choices">
            <div
                class="first-time-exam-ask__choice"
                :class="{
                    'first-time-exam-ask__choice--selected': hasTakenExam === false
                }"
                tabindex="0"
                @click="updateHasTakenExam(false)"
                @keydown.enter="updateHasTakenExam(false)"
                @mousedown.prevent
            >
                No
            </div>
            <div
                class="first-time-exam-ask__choice"
                :class="{
                    'first-time-exam-ask__choice--selected': hasTakenExam === true
                }"
                tabindex="0"
                @click="updateHasTakenExam(true)"
                @keydown.enter="updateHasTakenExam(true)"
                @mousedown.prevent
            >
                Yes, I've taken it before
            </div>
        </div>
        <PocketButton
            v-if="showingTryAQuestionNext"
            class="first-time-exam-ask__submit"
            :disabled="hasTakenExam === null"
            @click="submit"
        >
            Continue
        </PocketButton>
        <PocketButton
            v-else
            class="first-time-exam-ask__submit"
            :disabled="hasTakenExam === null"
            @click="submit"
        >
            Create Account<Icon class="first-time-exam-ask__submit-arrow" type="arrow" />
        </PocketButton>
        <PocketButton
            v-if="isMarketingCampaign"
            class="first-time-exam-ask__go-back"
            type="secondary"
            @click="goBack"
        >
            Select a different exam
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>
.first-time-exam-ask {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    &__choices {
        margin-top: 64px;
        width: 100%;
        max-width: 492px;

        @include breakpoint(black-bear) {
            margin-top: 44px;
            max-width: 315px;
        }
    }

    &__choice {
        display: flex;
        padding: 12px 15px;
        margin-bottom: 12px;
        border-radius: 6px;
        background: $white;
        box-sizing: border-box;
        position: relative;
        outline: none;
        cursor: pointer;

        &:last-child {
            margin-bottom: 0;
        }

        &::after {
            content: '';
            position: absolute;
            top: -1px;
            bottom: -1px;
            left: -1px;
            right: -1px;
            border: 0.5px solid rgba($pewter, 0.85);
            box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
            border-radius: 6px;
            pointer-events: none;
        }

        @media (hover: hover) {
            &:hover::after {
                display: block;
                top: -2px;
                bottom: -2px;
                left: -2px;
                right: -2px;
                border: 1.5px solid $slate;
                border-radius: 7px;
            }
        }

        &:focus::after {
            display: block;
            border: 1px solid $brand-blue;
        }

        &--selected {
            &::after {
                display: block;
                border: 2px solid $brand-blue;
            }

            @media (hover: hover) {
                &:hover::after {
                    border: 2px solid $brand-blue;
                }
            }
        }
    }

    &__submit {
        margin-top: 67px;

        @include breakpoint(black-bear) {
            margin-top: 48px;
        }
    }

    &__submit-arrow {
        margin-left: 8px;
    }

    &__go-back {
        margin-top: 20px;
    }
}
</style>
